import { ChevronDown, ChevronUp } from "lucide-react";
import Pagination from "./Pagination";
import EmptyState from "./EmptyState";

/**
 * Reusable admin table with column config, sort, row click, pagination, empty state.
 * Server-driven — parent owns the data, sort key, page, and pageSize.
 *
 * columns = [{ key, label, render?(row), sortable?: bool, className? }]
 */
export default function DataTable({
  columns,
  rows,
  total,
  page,
  pageSize,
  sortKey,
  sortOrder,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  loading = false,
  empty,
  testId = "data-table",
}) {
  const safeRows = rows || [];

  return (
    <div data-testid={testId}>
      <div className="bg-white border border-brand-linen overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-linen/30">
            <tr className="text-left text-xs uppercase tracking-wider text-brand-muted">
              {columns.map((col) => {
                const isSorted = onSortChange && sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    className={`px-4 py-3 ${col.className || ""} ${col.sortable && onSortChange ? "cursor-pointer select-none" : ""}`}
                    onClick={() => {
                      if (!col.sortable || !onSortChange) return;
                      if (isSorted) {
                        onSortChange(col.key, sortOrder === "desc" ? "asc" : "desc");
                      } else {
                        onSortChange(col.key, "desc");
                      }
                    }}
                    data-testid={`${testId}-col-${col.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {isSorted && (sortOrder === "desc"
                        ? <ChevronDown className="w-3 h-3" />
                        : <ChevronUp className="w-3 h-3" />)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-brand-muted">Loading…</td></tr>
            )}
            {!loading && safeRows.length === 0 && (
              <tr><td colSpan={columns.length} className="p-0">
                {empty || <EmptyState testId={`${testId}-empty`} title="No rows" />}
              </td></tr>
            )}
            {!loading && safeRows.map((row, i) => (
              <tr
                key={row.id || row.tx_id || row.event_id || i}
                data-testid={`${testId}-row-${row.id || row.tx_id || row.event_id || i}`}
                className={`border-t border-brand-linen/50 ${onRowClick ? "cursor-pointer hover:bg-brand-linen/15" : ""}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className={`px-4 py-3 ${col.className || ""}`}>
                    {col.render ? col.render(row) : (row[col.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {onPageChange && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total || 0}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
}
